/**
 * Utility function to extract error message from axios error
 * @param {Error} error - The error object
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error) => {
  if (!error) return 'ಅನಿರೀಕ್ಷಿತ ದೋಷ ಸಂಭವಿಸಿದೆ';

  // Handle network errors (backend offline)
  if (error.isNetworkError || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return error.message || 'ಸರ್ವರ್ ಸಂಪರ್ಕವಿಲ್ಲ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.';
  }

  // Handle axios response errors
  if (error.response) {
    // Validation errors array
    if (error.response.data?.errors && Array.isArray(error.response.data.errors)) {
      return error.response.data.errors
        .map(err => err.msg || err.message || JSON.stringify(err))
        .join(', ');
    }
    
    // Single error message
    if (error.response.data?.error) {
      return typeof error.response.data.error === 'string'
        ? error.response.data.error
        : JSON.stringify(error.response.data.error);
    }
    
    // Generic message
    if (error.response.data?.message) {
      return error.response.data.message;
    }
    
    // HTTP status specific messages
    if (error.response.status === 401) {
      return 'ದಯವಿಟ್ಟು ಮತ್ತೆ ಲಾಗ್ ಇನ್ ಆಗಿ.';
    }
    if (error.response.status === 403) {
      return 'ನೀವು ಈ ಕ್ರಿಯೆಯನ್ನು ನಿರ್ವಹಿಸಲು ಅನುಮತಿ ಹೊಂದಿಲ್ಲ.';
    }
    if (error.response.status === 404) {
      return 'ಡೇಟಾ ಕಂಡುಬಂದಿಲ್ಲ.';
    }
    if (error.response.status === 500) {
      return 'ಸರ್ವರ್ ದೋಷ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.';
    }
    if (error.response.status === 503) {
      return 'ಸರ್ವರ್ ಅನುಪಲಬ್ಧವಾಗಿದೆ. ದಯವಿಟ್ಟು ನಂತರ ಪ್ರಯತ್ನಿಸಿ.';
    }
  }
  
  // Connection timeout
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'ಸಂಪರ್ಕ ಸಮಯ ಮೀರಿದೆ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.';
  }
  
  // Network errors
  if (error.message) {
    // Check if it's already in Kannada
    if (error.message.includes('ಸರ್ವರ್') || error.message.includes('ಸಂಪರ್ಕ')) {
      return error.message;
    }
    return error.message;
  }
  
  return 'ಅನಿರೀಕ್ಷಿತ ದೋಷ ಸಂಭವಿಸಿದೆ';
};

/**
 * Format currency value to Indian Rupee format
 * @param {number|string} value - The value to format
 * @returns {string} - Formatted currency string
 */
export const formatRupee = (value) => {
  return '₹' + Math.round(Number(value || 0)).toLocaleString('en-IN');
};

/**
 * Format date to readable format
 * @param {string|Date} dateString - The date to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Parse number safely, returning 0 for invalid values
 * @param {any} value - The value to parse
 * @returns {number} - Parsed number or 0
 */
export const parseNum = (value) => {
  const v = parseFloat(value);
  return isFinite(v) ? v : 0;
};

